import { Body, H7, Label, Stack, useModals } from '@qred/components-library';
import React, { MouseEvent, useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { FormStatus, ValidationContext } from '~/components/hoc/withValidation';
import Button, { ButtonType } from '~/components/shared/Button/button';
import CheckBox from '~/components/shared/CheckBox/CheckBox';
import Icon, { SvgSrc } from '~/components/shared/Icon/Icon';
import Group from '~/components/shared/Layout/Group';
import SpinnerCircular from '~/components/shared/Spinner/SpinnerCircular';
import NextButton from '~/components/shared/Stepper/components/NextButton';
import { companyIsSoleTrader } from '~/constants/markets';
import { ClientType, CountryCode, FlexContentPlacement } from '~/enums';
import { getGuarantorText } from '~/helpers/shared.helper';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import useDateFormatter from '~/hooks/useDateFormatter';
import useMarketConfig from '~/hooks/useMarketConfig';
import useModalsOld from '~/hooks/useModals';
import useTranslate from '~/hooks/useTranslate';
import { ILoanApplicationGuarantor } from '~/interfaces/loanApplication';
import { RootState } from '~/store';
import { pushToGtmLoanAction } from '~/store/actions/gtmActions';
import {
  getGuarantorsAndBoardMembers,
  getGuarantorsAndBoardMembersSvcOnboarding,
} from '~/store/actions/loanApplicationActions';
import { updateForm } from '~/store/slices/loanApplication.slice';
import { ApiStatus } from '~/store/types/sharedTypes';
import {
  StandardStepContent,
  StandardStepContentBody,
  StandardStepContentFooter,
} from '~/styles/BaseStyledComponents';
import { ComponentSize } from '~/styles/BaseStyledComponentsOld';
import { theme } from '~/styles/themes';
import { onChangeInputEvent } from '~/types/types';
import useGoToNextStep from '../Steps/useGoToNextStep';
import AddOrEditGuarantorModalBody from './AddOrEditGuarantorModalBody';
import { EditGuarantorModalDE } from './EditGuarantorModalDE';
import { CheckboxWrapper } from './SelectGuarantor.styled';
import SelectGuarantorHeader from './components/SelectGuarantorHeader';
import SelectGuarantorNetherlandsFooter from './components/SelectGuarantorNetherlandsFooter';

const SelectGuarantor = () => {
  const t = useTranslate();
  const dispatch = useAppDispatch();
  const modals = useModals();
  const modalsOld = useModalsOld();

  const formatDate = useDateFormatter();

  const { marketsHasAddGuarantorButtonNonFooter } = useMarketConfig();

  const validationContext = useContext(ValidationContext);

  const {
    loanApplication: { form, apiStatus },
    intl: { market },
    matchedMedia,
  } = useSelector((state: RootState) => state);
  const onNextStepClick = useGoToNextStep();

  const companyHasBoardMembers = !companyIsSoleTrader(market, form.companyType);
  const guarantorApiHasNotBeenCalled = apiStatus.guarantors === ApiStatus.Idle;

  const shouldCallGuarantorApi =
    guarantorApiHasNotBeenCalled &&
    (companyHasBoardMembers || form.clientType === ClientType.RLC);

  const shouldNotCallGuarantorApi =
    market === CountryCode.BE && form.clientType !== ClientType.RLC;

  /**
   * Sole traders do not have board members, but they can have existing guarantors
   * if they are a RLC
   */
  useEffect(() => {
    if (shouldNotCallGuarantorApi) {
      return;
    }
    if (shouldCallGuarantorApi) {
      if (market === CountryCode.DE) {
        dispatch(getGuarantorsAndBoardMembersSvcOnboarding());
      } else {
        dispatch(getGuarantorsAndBoardMembers());
      }
    }
  }, [dispatch, shouldCallGuarantorApi, shouldNotCallGuarantorApi]);

  const handleOpenAddOrEditGuarantorModal = (
    guarantor?: ILoanApplicationGuarantor
  ) => {
    validationContext.setFormStatus(FormStatus.IDLE);
    const modalTitleKey = getGuarantorText(guarantor);
    if (market === CountryCode.DE) {
      if (guarantor) {
        modals.openModal({
          id: 'editGuarantor-modal',
          title: <H7>{t('Onboarding.EditContactInformation')}</H7>,
          children: <EditGuarantorModalDE initialGuarantor={guarantor} />,
          size: 'lg',
          fullScreen: matchedMedia.smallScreenPortrait,
        });
      }
    } else {
      modalsOld.openModal({
        id: 'addOrEditGuarantor-modal',
        header: <H7>{t(`Onboarding.${modalTitleKey}`)}</H7>,
        children: <AddOrEditGuarantorModalBody initialGuarantor={guarantor} />,
        size: 'md',
        fullScreen: matchedMedia.smallScreenPortrait,
        fixedHeaderAndFooter: true,
        hasOwnFooter: true,
        filledBackground: true,
      });
    }

    dispatch(
      pushToGtmLoanAction({
        actionName: `${
          guarantor ? 'open_edit_guarantor_modal' : 'open_add_guarantor_modal'
        }`,
      })
    );
  };

  const handleOnChange = (
    event: onChangeInputEvent,
    guarantor: ILoanApplicationGuarantor
  ) => {
    if (!guarantor.email || !guarantor.phone) {
      // open edit modal
      handleOpenAddOrEditGuarantorModal(guarantor);
      return;
    }
    const checkedGuarantors = (
      form.guarantors || []
    ).map((g: ILoanApplicationGuarantor) =>
      g.guarantorId === guarantor.guarantorId
        ? { ...g, isChecked: event.target.checked }
        : { ...g }
    );

    dispatch(
      updateForm({
        guarantors: checkedGuarantors,
      })
    );
    dispatch(
      pushToGtmLoanAction({
        actionName: 'select_guarantor',
        stepActionNumber: 0.1,
      })
    );
  };

  return (
    <StandardStepContent>
      <StandardStepContentBody>
        <SelectGuarantorHeader />
        {apiStatus.guarantors === ApiStatus.Started ? (
          <SpinnerCircular color={theme.colors.secondaryGray} />
        ) : (
          <Stack>
            {form.guarantors &&
              form.guarantors.map((guarantor: ILoanApplicationGuarantor) => (
                <CheckboxWrapper key={guarantor.guarantorId}>
                  <CheckBox
                    checked={guarantor.isChecked || false}
                    onChange={(event) => handleOnChange(event, guarantor)}
                    name={guarantor.personalNumber}
                    disabled={guarantor.latestGuarantor}
                    explanation={
                      guarantor.latestGuarantor
                        ? (t('Onboarding.PreSelectedGuarantor') as string)
                        : ''
                    }
                    align={FlexContentPlacement.Center}
                  >
                    <Stack spacing="xs">
                      <Label size="lg">{guarantor.fullName}</Label>
                      {(!!guarantor.maskedPersonalNumber ||
                        !!guarantor.personalNumber) && (
                        <Body size="md">
                          {guarantor.personalNumber ||
                            guarantor.maskedPersonalNumber}
                        </Body>
                      )}

                      {(!!guarantor.dateOfBirth ||
                        !!guarantor.maskedDateOfBirth) && (
                        <Body size="md">
                          {guarantor.dateOfBirth
                            ? formatDate(guarantor.dateOfBirth, {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                              })
                            : guarantor.maskedDateOfBirth}
                        </Body>
                      )}

                      {!guarantor.latestGuarantor && (
                        <Button
                          dataCy="select_guarantor_step_edit_button"
                          onClick={(e: MouseEvent) => {
                            e.preventDefault();
                            handleOpenAddOrEditGuarantorModal(guarantor);
                          }}
                          type={ButtonType.Text}
                        >
                          {t(`Onboarding.${getGuarantorText(guarantor)}`)}
                        </Button>
                      )}
                    </Stack>
                  </CheckBox>
                </CheckboxWrapper>
              ))}

            {marketsHasAddGuarantorButtonNonFooter && (
              <Button
                type={ButtonType.Tertiary}
                dataCy="select_guarantor_step_add_button"
                mt={1.5}
                onClick={() => handleOpenAddOrEditGuarantorModal()}
              >
                <Group spacing="sm" align="center" justify="center">
                  <Icon src={SvgSrc.Plus} size={ComponentSize.Small} />
                  {t('Onboarding.AddNewGuarantor')}
                </Group>
              </Button>
            )}
          </Stack>
        )}
      </StandardStepContentBody>
      <StandardStepContentFooter>
        {market === CountryCode.NL ? (
          <SelectGuarantorNetherlandsFooter />
        ) : (
          <NextButton
            label="Onboarding.Next"
            action={onNextStepClick}
            isLoading={apiStatus.patch === ApiStatus.Started}
          />
        )}
      </StandardStepContentFooter>
    </StandardStepContent>
  );
};

export default SelectGuarantor;
